/**
 * UI Overlay Renderer
 * Validates Requirements 3.2.8, 3.2.9, 4.2.3, 4.2.4
 */

import type p5 from 'p5';
import { AppState, DisplayMode, GestureType, PersonCollection, PersonID, VisualizationType } from '../../../types';
import { PerformanceMonitor } from '../../../utils/PerformanceMonitor';

/**
 * A clickable hit region for a per-person visualization selector arrow.
 */
interface VizSelectorHit {
  personId: PersonID;
  direction: 1 | -1;
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Short, human-friendly labels for each visualization type, shown in the selector.
 */
const VIZ_LABELS: Record<VisualizationType, string> = {
  [VisualizationType.PARTICLE_TRAILS]: 'Particles',
  [VisualizationType.GEOMETRIC_SHAPES]: 'Circles',
  [VisualizationType.FLOWING_RIBBONS]: 'Ribbons',
  [VisualizationType.DEPARTURE_BOARD]: 'Board',
};

/**
 * Renders UI overlay elements including mode toggle button,
 * landmark labels, FPS counter, and gesture indicators
 */
export class UIOverlay {
  // Button configuration
  private readonly buttonWidth = 140;
  private readonly buttonHeight = 40;
  private readonly buttonPadding = 20;
  private buttonX = 0;
  private buttonY = 0;
  private isHovered = false;

  // Debug button configuration
  private readonly debugButtonWidth = 100;
  private readonly debugButtonHeight = 40;
  private debugButtonX = 0;
  private debugButtonY = 0;
  private isDebugButtonHovered = false;

  // Label configuration
  private readonly labelFontSize = 14;
  private readonly labelOffset = 10;

  // FPS counter configuration
  private readonly fpsX = 20;
  private readonly fpsY = 30;
  
  // Performance metrics configuration
  private readonly metricsX = 20;
  private readonly metricsY = 60;

  // Gesture display configuration
  private readonly gestureX = 20;
  private gestureY = 60; // Will be adjusted based on metrics display
  
  // Gesture flash effect
  private gestureFlashTimestamps: Map<GestureType, number> = new Map();
  private readonly flashDuration = 500; // ms
  private lastActiveGestures: Set<GestureType> = new Set();
  
  // Color palette for gestures (6 colors that rotate) - Enhanced for better visibility
  private readonly gestureColorPalette: [number, number, number][] = [
    [255, 180, 80],  // Warm Orange
    [80, 180, 255],  // Sky Blue
    [255, 80, 180],  // Hot Pink
    [80, 255, 180],  // Mint Green
    [180, 80, 255],  // Purple
    [255, 255, 80],  // Bright Yellow
  ];
  
  // Performance monitor reference
  private performanceMonitor: PerformanceMonitor | null = null;
  
  // Per-person visualization selector hit regions, rebuilt each frame.
  private vizSelectorHits: VizSelectorHit[] = [];
  
  /**
   * Set performance monitor for metrics display
   */
  setPerformanceMonitor(monitor: PerformanceMonitor): void {
    this.performanceMonitor = monitor;
  }
  /**
   * Render the UI overlay based on display mode
   */
  render(p: p5, state: AppState): void {
    // Track new gestures for flash effect (aggregated across all tracked people)
    this.updateGestureFlash(this.collectActiveGestures(state.people));
    
    // Always render the mode toggle button
    this.renderModeToggleButton(p, state.displayMode);

    // Render debug toggle button (only in ARTISTIC mode)
    // Validates Requirements 3.2.23, 4.2.5, 4.2.6
    if (state.displayMode === DisplayMode.ARTISTIC) {
      this.renderDebugToggleButton(p, state.debugOverlayEnabled);
    }

    // Render the per-person visualization selector in ARTISTIC mode, where the
    // chosen visualization is actually visible. Rebuilds clickable hit regions.
    this.vizSelectorHits = [];
    if (state.displayMode === DisplayMode.ARTISTIC) {
      this.renderVisualizationSelectors(p, state.people);
    }

    // Render gesture flash effect in both modes (visual feedback)
    this.renderGestureFlash(p);

    // Render FPS warning in both modes (Requirements 7.2, 7.3)
    this.renderFpsWarning(p);

    // Only render analysis overlays in POSE_ANALYSIS mode
    if (state.displayMode === DisplayMode.POSE_ANALYSIS) {
      this.renderFPSCounter(p);
      
      // Render performance metrics
      const metricsHeight = this.renderPerformanceMetrics(p);
      
      // Adjust gesture display position based on metrics height
      this.gestureY = this.metricsY + metricsHeight + 10;
      
      // Render gesture indicators and landmark labels for all tracked people
      this.renderMultiPersonGestureIndicators(p, state.people);
      this.renderMultiPersonLandmarkLabels(p, state.people);
    }
  }

  /**
   * Aggregate the active gestures of every tracked person into a single set.
   * Used to drive the gesture flash effect across all people.
   */
  private collectActiveGestures(people: AppState['people']): Set<GestureType> {
    const aggregated = new Set<GestureType>();
    people.people.forEach(person => {
      person.gestureState.activeGestures.forEach(gesture => aggregated.add(gesture));
    });
    return aggregated;
  }

  /**
   * Update gesture flash tracking
   */
  private updateGestureFlash(activeGestures: Set<GestureType>): void {
    const now = Date.now();
    
    // Detect newly triggered gestures
    activeGestures.forEach(gesture => {
      if (!this.lastActiveGestures.has(gesture)) {
        // New gesture detected - start flash
        this.gestureFlashTimestamps.set(gesture, now);
      }
    });
    
    // Clean up old flash timestamps
    this.gestureFlashTimestamps.forEach((timestamp, gesture) => {
      if (now - timestamp > this.flashDuration) {
        this.gestureFlashTimestamps.delete(gesture);
      }
    });
    
    // Update last active gestures
    this.lastActiveGestures = new Set(activeGestures);
  }

  /**
   * Get color for a gesture based on its index in the color palette
   */
  private getGestureColor(gesture: GestureType): [number, number, number] {
    // Get all gesture types in a consistent order
    const allGestureTypes = Object.values(GestureType);
    const gestureIndex = allGestureTypes.indexOf(gesture);
    
    // Use modulo to rotate through the 6-color palette
    const colorIndex = gestureIndex % this.gestureColorPalette.length;
    const color = this.gestureColorPalette[colorIndex];
    
    // Fallback to white if color not found (should never happen)
    return color ?? [255, 255, 255];
  }

  /**
   * Render gesture flash effect (visual feedback when gesture triggers)
   */
  private renderGestureFlash(p: p5): void {
    const now = Date.now();
    
    this.gestureFlashTimestamps.forEach((timestamp, gesture) => {
      const elapsed = now - timestamp;
      if (elapsed < this.flashDuration) {
        // Calculate fade alpha (1 -> 0)
        const alpha = 1 - (elapsed / this.flashDuration);
        
        // Draw flash overlay
        p.push();
        p.noStroke();
        
        // Get color from palette
        const flashColor = this.getGestureColor(gesture);
        
        // Draw border flash
        const borderWidth = 10;
        const flashAlpha = alpha * 100; // Max alpha of 100
        
        p.fill(flashColor[0], flashColor[1], flashColor[2], flashAlpha);
        
        // Top border
        p.rect(0, 0, p.width, borderWidth);
        // Bottom border
        p.rect(0, p.height - borderWidth, p.width, borderWidth);
        // Left border
        p.rect(0, 0, borderWidth, p.height);
        // Right border
        p.rect(p.width - borderWidth, 0, borderWidth, p.height);
        
        p.pop();
      }
    });
  }

  /**
   * Render the mode toggle button
   */
  private renderModeToggleButton(p: p5, mode: DisplayMode): void {
    // Calculate button position (top-right corner)
    this.buttonX = p.width - this.buttonWidth - this.buttonPadding;
    this.buttonY = this.buttonPadding;

    // Check if mouse is hovering over button
    this.isHovered = this.isMouseOverButton(p);

    // Draw button with enhanced styling
    p.push();
    
    // Draw button shadow for depth
    p.noStroke();
    p.fill(0, 0, 0, 100);
    p.rect(this.buttonX + 2, this.buttonY + 2, this.buttonWidth, this.buttonHeight, 10);
    
    // Draw button background
    if (this.isHovered) {
      p.fill(70, 70, 90, 240); // Lighter on hover
    } else {
      p.fill(40, 40, 60, 220); // Semi-transparent dark background
    }
    
    p.rect(this.buttonX, this.buttonY, this.buttonWidth, this.buttonHeight, 10);

    // Draw button border for polish (if noFill is available)
    if (typeof p.noFill === 'function') {
      p.noFill();
      p.stroke(255, 255, 255, 100);
      p.strokeWeight(1);
      p.rect(this.buttonX, this.buttonY, this.buttonWidth, this.buttonHeight, 10);
    }

    // Draw button text
    p.noStroke();
    p.fill(255, 255, 255);
    p.textAlign(p.CENTER, p.CENTER);
    p.textSize(15);
    p.textStyle(p.BOLD);
    
    const buttonText = mode === DisplayMode.ARTISTIC ? 'Analysis Mode' : 'Artistic Mode';
    p.text(buttonText, this.buttonX + this.buttonWidth / 2, this.buttonY + this.buttonHeight / 2);
    
    p.pop();
  }

  /**
   * Render the debug toggle button
   * Only visible in ARTISTIC mode
   * Validates Requirements 3.2.23, 4.2.5, 4.2.6
   */
  private renderDebugToggleButton(p: p5, debugEnabled: boolean): void {
    // Calculate button position (top-right corner, 60px below mode toggle)
    this.debugButtonX = p.width - this.debugButtonWidth - this.buttonPadding;
    this.debugButtonY = this.buttonPadding + this.buttonHeight + 20; // 20px gap below mode toggle

    // Check if mouse is hovering over debug button
    this.isDebugButtonHovered = this.isMouseOverDebugButton(p);

    // Draw button with enhanced styling
    p.push();
    
    // Draw button shadow for depth
    p.noStroke();
    p.fill(0, 0, 0, 100);
    p.rect(this.debugButtonX + 2, this.debugButtonY + 2, this.debugButtonWidth, this.debugButtonHeight, 10);
    
    // Draw button background
    let bgAlpha = 200;
    if (this.isDebugButtonHovered) {
      bgAlpha = 240; // Lighter on hover
    }
    p.fill(40, 40, 50, bgAlpha); // Semi-transparent dark gray
    
    p.rect(this.debugButtonX, this.debugButtonY, this.debugButtonWidth, this.debugButtonHeight, 10);

    // Draw button border (thicker when enabled)
    p.noFill();
    if (debugEnabled) {
      p.stroke(255, 255, 255, 255); // White border when enabled
      p.strokeWeight(2);
    } else {
      p.stroke(150, 150, 150, 200); // Gray border when disabled
      p.strokeWeight(1);
    }
    p.rect(this.debugButtonX, this.debugButtonY, this.debugButtonWidth, this.debugButtonHeight, 10);

    // Draw button text
    p.noStroke();
    p.fill(255, 255, 255);
    p.textAlign(p.CENTER, p.CENTER);
    p.textSize(14);
    p.textStyle(p.NORMAL);
    
    const buttonText = debugEnabled ? 'Debug: ON' : 'Debug: OFF';
    p.text(buttonText, this.debugButtonX + this.debugButtonWidth / 2, this.debugButtonY + this.debugButtonHeight / 2);
    
    p.pop();
  }

  /**
   * Render FPS counter
   */
  private renderFPSCounter(p: p5): void {
    const fps = Math.round(p.frameRate());
    
    p.push();
    p.fill(255, 255, 255);
    p.textAlign(p.LEFT, p.TOP);
    p.textSize(16);
    p.textStyle(p.BOLD);
    
    // Color code FPS: green if good, yellow if ok, red if poor
    if (fps >= 25) {
      p.fill(100, 255, 100); // Green
    } else if (fps >= 15) {
      p.fill(255, 255, 100); // Yellow
    } else {
      p.fill(255, 100, 100); // Red
    }
    
    p.text(`FPS: ${fps}`, this.fpsX, this.fpsY);
    p.pop();
  }

  /**
   * Render FPS warning when performance drops below threshold
   * Shows in both POSE_ANALYSIS and ARTISTIC modes
   * Validates Requirements 7.2, 7.3
   */
  private renderFpsWarning(p: p5): void {
    if (!this.performanceMonitor) {
      return;
    }

    if (this.performanceMonitor.shouldShowFpsWarning()) {
      const warningMessage = 'Performance warning: FPS below 20. Consider reducing the number of people in frame.';
      
      p.push();
      
      // Position at bottom center of screen
      const warningX = p.width / 2;
      const warningY = p.height - 40;
      
      // Measure text for background box
      p.textSize(14);
      p.textStyle(p.NORMAL);
      const textWidth = p.textWidth(warningMessage);
      const padding = 12;
      const boxHeight = 24;
      
      // Draw semi-transparent background
      p.fill(0, 0, 0, 200);
      p.noStroke();
      p.rect(
        warningX - textWidth / 2 - padding,
        warningY - boxHeight / 2 - padding / 2,
        textWidth + padding * 2,
        boxHeight + padding,
        5
      );
      
      // Draw warning border
      p.noFill();
      p.stroke(255, 100, 100);
      p.strokeWeight(2);
      p.rect(
        warningX - textWidth / 2 - padding,
        warningY - boxHeight / 2 - padding / 2,
        textWidth + padding * 2,
        boxHeight + padding,
        5
      );
      
      // Draw warning text
      p.noStroke();
      p.fill(255, 100, 100);
      p.textAlign(p.CENTER, p.CENTER);
      p.textSize(14);
      p.textStyle(p.BOLD);
      p.text(warningMessage, warningX, warningY);
      
      p.pop();
    }
  }
  
  /**
   * Render performance metrics
   * Returns the height of the metrics display
   */
  private renderPerformanceMetrics(p: p5): number {
    if (!this.performanceMonitor) {
      return 0;
    }
    
    const metrics = this.performanceMonitor.getCurrentMetrics();
    const poseStats = this.performanceMonitor.getPoseDetectionStats();
    const latencyStats = this.performanceMonitor.getLatencyStats();
    
    p.push();
    p.textAlign(p.LEFT, p.TOP);
    p.textSize(12);
    p.textStyle(p.NORMAL);
    
    let yOffset = 0;
    const lineHeight = 16;
    
    // Pose Detection FPS
    const poseFps = poseStats.average.toFixed(1);
    const poseFpsColor: [number, number, number] = poseStats.average >= 20 ? [100, 255, 100] : [255, 100, 100];
    p.fill(poseFpsColor[0]!, poseFpsColor[1]!, poseFpsColor[2]!);
    p.text(`Pose: ${poseFps} fps`, this.metricsX, this.metricsY + yOffset);
    yOffset += lineHeight;
    
    // Latency
    const latency = latencyStats.average.toFixed(1);
    const latencyColor: [number, number, number] = latencyStats.average <= 100 ? [100, 255, 100] : [255, 100, 100];
    p.fill(latencyColor[0]!, latencyColor[1]!, latencyColor[2]!);
    p.text(`Latency: ${latency}ms`, this.metricsX, this.metricsY + yOffset);
    yOffset += lineHeight;
    
    // Frame time budget
    const frameTime = metrics.frameTime.toFixed(1);
    const frameTimeColor: [number, number, number] = metrics.frameTime <= 50 ? [100, 255, 100] : [255, 100, 100];
    p.fill(frameTimeColor[0]!, frameTimeColor[1]!, frameTimeColor[2]!);
    p.text(`Frame: ${frameTime}ms`, this.metricsX, this.metricsY + yOffset);
    yOffset += lineHeight;
    
    // Performance warning if below requirements
    if (!this.performanceMonitor.meetsRequirements()) {
      p.fill(255, 100, 100);
      p.textStyle(p.BOLD);
      p.text('⚠ Performance Warning', this.metricsX, this.metricsY + yOffset);
      yOffset += lineHeight;
    }
    
    p.pop();
    
    return yOffset;
  }

  /**
   * Render gesture indicators for all people in multi-person mode
   * Displays gestures for each person with their assigned colors
   * Validates Requirements 8.1, 8.3
   */
  private renderMultiPersonGestureIndicators(p: p5, people: PersonCollection): void {
    if (people.people.size === 0) {
      // Show "No people detected" message
      p.push();
      p.fill(150, 150, 150);
      p.textAlign(p.LEFT, p.TOP);
      p.textSize(14);
      p.text('No people detected', this.gestureX, this.gestureY);
      p.pop();
      return;
    }

    p.push();
    p.textAlign(p.LEFT, p.TOP);
    p.textSize(14);
    
    let yOffset = 0;
    
    // Iterate over all people
    people.people.forEach((person, personId) => {
      const activeGestures = person.gestureState.activeGestures;
      
      // Draw person header with their assigned color
      const personColor = person.visualConfig.primaryColor;
      p.fill(personColor.r, personColor.g, personColor.b);
      p.textStyle(p.BOLD);
      
      const personLabel = `Person ${personId.substring(0, 8)}:`;
      p.text(personLabel, this.gestureX, this.gestureY + yOffset);
      yOffset += 22;
      
      // Draw gestures for this person
      if (activeGestures.size === 0) {
        p.fill(150, 150, 150);
        p.textStyle(p.NORMAL);
        p.text('  No gestures', this.gestureX, this.gestureY + yOffset);
        yOffset += 22;
      } else {
        activeGestures.forEach((gesture) => {
          const gestureText = gesture;
          
          // Draw background box for better visibility
          const textWidth = p.textWidth(`  ${gestureText}`);
          const padding = 6;
          const boxHeight = 18;
          
          p.fill(0, 0, 0, 180);
          p.noStroke();
          p.rect(
            this.gestureX - padding,
            this.gestureY + yOffset - padding,
            textWidth + padding * 2,
            boxHeight + padding,
            3
          );
          
          // Use person's assigned color for gesture text
          p.fill(personColor.r, personColor.g, personColor.b);
          p.textStyle(p.NORMAL);
          p.text(`  ${gestureText}`, this.gestureX, this.gestureY + yOffset);
          yOffset += 22;
        });
      }
      
      // Add spacing between people
      yOffset += 10;
    });
    
    p.pop();
  }

  /**
   * Render landmark labels for all people in multi-person mode
   * Uses each person's assigned colors for labels
   * Validates Requirements 8.2
   */
  private renderMultiPersonLandmarkLabels(p: p5, people: PersonCollection): void {
    // Define landmarks to label with their display names
    const landmarksToLabel = [
      { key: 'nose', label: 'Head' },
      { key: 'leftShoulder', label: 'L Shoulder' },
      { key: 'rightShoulder', label: 'R Shoulder' },
      { key: 'leftElbow', label: 'L Elbow' },
      { key: 'rightElbow', label: 'R Elbow' },
      { key: 'leftWrist', label: 'L Wrist' },
      { key: 'rightWrist', label: 'R Wrist' },
      { key: 'leftHip', label: 'L Hip' },
      { key: 'rightHip', label: 'R Hip' },
      { key: 'leftKnee', label: 'L Knee' },
      { key: 'rightKnee', label: 'R Knee' },
      { key: 'leftAnkle', label: 'L Ankle' },
      { key: 'rightAnkle', label: 'R Ankle' }
    ];

    p.push();
    p.textAlign(p.CENTER, p.CENTER);
    p.textSize(this.labelFontSize);
    p.textStyle(p.BOLD);

    // Iterate over all people
    people.people.forEach((person) => {
      const pose = person.poseData;
      const personColor = person.visualConfig.primaryColor;
      
      if (!pose.detected) {
        return;
      }

      // Render each label with person's assigned color
      landmarksToLabel.forEach(({ key, label }) => {
        const landmark = pose.landmarks[key as keyof typeof pose.landmarks];
        
        // Only render if landmark is visible
        if (landmark && landmark.visibility > 0.5) {
          const x = landmark.x * p.width;
          const y = landmark.y * p.height;

          // Draw background for better readability
          const textWidth = p.textWidth(label);
          const padding = 5;
          
          // Draw shadow for depth
          p.fill(0, 0, 0, 200);
          p.noStroke();
          p.rect(
            x - textWidth / 2 - padding + 1,
            y - this.labelOffset - this.labelFontSize / 2 - padding + 1,
            textWidth + padding * 2,
            this.labelFontSize + padding * 2,
            4
          );
          
          // Draw background
          p.fill(0, 0, 0, 220);
          p.rect(
            x - textWidth / 2 - padding,
            y - this.labelOffset - this.labelFontSize / 2 - padding,
            textWidth + padding * 2,
            this.labelFontSize + padding * 2,
            4
          );

          // Draw label text with person's assigned color
          p.fill(personColor.r, personColor.g, personColor.b);
          p.text(label, x, y - this.labelOffset);
        }
      });
    });

    p.pop();
  }

  /**
   * Render a compact visualization selector above each detected person.
   *
   * Each selector shows the person's current visualization label flanked by
   * "‹" and "›" arrows. Clicking an arrow cycles that person's visualization
   * (handled via handleClick). The selector is anchored above the person's nose
   * (or shoulder midpoint as a fallback) so it tracks them on screen.
   *
   * Clickable hit regions are recorded in this.vizSelectorHits for hit-testing.
   */
  private renderVisualizationSelectors(p: p5, people: PersonCollection): void {
    const arrowSize = 26;
    const labelWidth = 96;
    const gap = 6;
    const totalWidth = arrowSize + gap + labelWidth + gap + arrowSize;
    const height = 26;

    p.push();
    p.textAlign(p.CENTER, p.CENTER);

    people.people.forEach((person, personId) => {
      const pose = person.poseData;
      if (!pose.detected) {
        return;
      }

      // Anchor point: nose if visible, else shoulder midpoint, else skip.
      let anchorX: number;
      let anchorY: number;
      const nose = pose.landmarks.nose;
      const ls = pose.landmarks.leftShoulder;
      const rs = pose.landmarks.rightShoulder;
      if (nose.visibility > 0.5) {
        anchorX = nose.x * p.width;
        anchorY = nose.y * p.height;
      } else if (ls.visibility > 0.5 && rs.visibility > 0.5) {
        anchorX = ((ls.x + rs.x) / 2) * p.width;
        anchorY = ((ls.y + rs.y) / 2) * p.height;
      } else {
        return;
      }

      // Position the selector above the anchor, clamped to stay on screen.
      let left = anchorX - totalWidth / 2;
      let top = anchorY - 70;
      left = Math.max(10, Math.min(left, p.width - totalWidth - 10));
      top = Math.max(10, Math.min(top, p.height - height - 10));

      const color = person.visualConfig.primaryColor;

      // Left arrow (previous)
      const leftArrowX = left;
      this.drawSelectorArrow(p, leftArrowX, top, arrowSize, height, '‹', color);
      this.vizSelectorHits.push({ personId, direction: -1, x: leftArrowX, y: top, w: arrowSize, h: height });

      // Center label (current visualization)
      const labelX = leftArrowX + arrowSize + gap;
      p.noStroke();
      p.fill(0, 0, 0, 200);
      p.rect(labelX, top, labelWidth, height, 5);
      p.fill(color.r, color.g, color.b);
      p.textSize(13);
      p.textStyle(p.BOLD);
      p.text(VIZ_LABELS[person.visualizationType], labelX + labelWidth / 2, top + height / 2);

      // Right arrow (next)
      const rightArrowX = labelX + labelWidth + gap;
      this.drawSelectorArrow(p, rightArrowX, top, arrowSize, height, '›', color);
      this.vizSelectorHits.push({ personId, direction: 1, x: rightArrowX, y: top, w: arrowSize, h: height });
    });

    p.pop();
  }

  /**
   * Draw a single selector arrow button (background, border, glyph).
   */
  private drawSelectorArrow(
    p: p5,
    x: number,
    y: number,
    w: number,
    h: number,
    glyph: string,
    color: { r: number; g: number; b: number }
  ): void {
    const hovered = p.mouseX >= x && p.mouseX <= x + w && p.mouseY >= y && p.mouseY <= y + h;
    p.noStroke();
    p.fill(0, 0, 0, hovered ? 230 : 180);
    p.rect(x, y, w, h, 5);
    p.stroke(color.r, color.g, color.b, hovered ? 255 : 160);
    p.strokeWeight(1);
    p.noFill();
    p.rect(x, y, w, h, 5);
    p.noStroke();
    p.fill(255, 255, 255);
    p.textSize(18);
    p.textStyle(p.BOLD);
    p.text(glyph, x + w / 2, y + h / 2 - 1);
  }

  /**
   * Test whether a point falls within any visualization-selector arrow.
   * @returns The matching hit (personId + direction) or null.
   */
  private hitTestVizSelector(mouseX: number, mouseY: number): { personId: PersonID; direction: 1 | -1 } | null {
    for (const hit of this.vizSelectorHits) {
      if (mouseX >= hit.x && mouseX <= hit.x + hit.w && mouseY >= hit.y && mouseY <= hit.y + hit.h) {
        return { personId: hit.personId, direction: hit.direction };
      }
    }
    return null;
  }

  /**
   * Check if mouse is over the toggle button
   */
  private isMouseOverButton(p: p5): boolean {
    return (
      p.mouseX >= this.buttonX &&
      p.mouseX <= this.buttonX + this.buttonWidth &&
      p.mouseY >= this.buttonY &&
      p.mouseY <= this.buttonY + this.buttonHeight
    );
  }

  /**
   * Check if mouse is over the debug toggle button
   */
  private isMouseOverDebugButton(p: p5): boolean {
    return (
      p.mouseX >= this.debugButtonX &&
      p.mouseX <= this.debugButtonX + this.debugButtonWidth &&
      p.mouseY >= this.debugButtonY &&
      p.mouseY <= this.debugButtonY + this.debugButtonHeight
    );
  }

  /**
   * Handle mouse click events.
   * Returns:
   * - 'debug' if the debug toggle button was clicked
   * - true if the mode toggle button was clicked
   * - a { personId, direction } object if a visualization selector arrow was clicked
   * - false if nothing was clicked
   * Validates Requirements 3.2.23
   */
  handleClick(p: p5): boolean | 'debug' | { personId: PersonID; direction: 1 | -1 } {
    // Check debug button first (only in ARTISTIC mode, but we check anyway)
    if (this.isMouseOverDebugButton(p)) {
      return 'debug';
    }
    
    // Check mode toggle button
    if (this.isMouseOverButton(p)) {
      return true;
    }
    
    // Check per-person visualization selector arrows
    const vizHit = this.hitTestVizSelector(p.mouseX, p.mouseY);
    if (vizHit) {
      return vizHit;
    }
    
    return false;
  }

  /**
   * Update cursor style based on hover state
   */
  updateCursor(p: p5): void {
    const overVizSelector = this.hitTestVizSelector(p.mouseX, p.mouseY) !== null;
    if (this.isHovered || this.isDebugButtonHovered || overVizSelector) {
      p.cursor(p.HAND);
    } else {
      p.cursor(p.ARROW);
    }
  }
}
